// src/components/FlightFooter.jsx
// R7: Dynamic flight routes footer for SEO
// Updated to use global geolocation with Amadeus airport detection
// FIXED: Added safeText to prevent React Error #31

import React from 'react';
import './FlightFooter.css';
import { getLocationCity, hasValidAirport } from '../utils/geolocation';

// Safe text helper - prevents React Error #31
const safeText = (value, fallback = '') => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value.city) return String(value.city);
    if (value.name) return String(value.name);
    if (value.code) return String(value.code);
    if (value.text) return String(value.text);
    return fallback;
  }
  return String(value);
};

// ============================================================================
// POPULAR DESTINATIONS FOR ROUTES
// ============================================================================
const popularDestinations = [
  'Los Angeles',
  'Miami',
  'Las Vegas',
  'Orlando',
  'San Francisco',
  'Seattle',
  'Chicago',
  'Boston',
  'Denver',
  'Atlanta',
  'Phoenix',
  'Dallas',
];

// ============================================================================
// COMPONENT
// ============================================================================
function FlightFooter({ userLocation, onRouteClick }) {
  // Get the user's city
  const userCity = safeText(getLocationCity(userLocation));
  const hasAirport = hasValidAirport(userLocation);
  
  // Use detected city or fallback to "New York"
  const originCity = userCity || 'New York';
  
  // Safe airport code extraction
  const airportCode = safeText(userLocation?.airport, '');
  
  // Filter out user's city from destinations
  const destinations = popularDestinations.filter(
    dest => dest.toLowerCase() !== originCity.toLowerCase()
  );
  
  // Take top 12 routes
  const displayedRoutes = destinations.slice(0, 12);

  const handleRouteClick = (destination) => {
    if (onRouteClick) {
      onRouteClick({
        from: airportCode || originCity,
        to: destination,
        fromCity: originCity,
        toCity: destination
      });
    }
  };

  return (
    <footer className="flight-footer">
      <div className="flight-footer-content">
        <h3 className="flight-footer-title">Popular Flight Routes</h3>
        
        {/* Show detected location */}
        {userCity && hasAirport && airportCode && (
          <p className="flight-footer-location">
            ✈️ Showing routes from {userCity} ({airportCode})
          </p>
        )}
        
        <div className="flight-routes-grid">
          {displayedRoutes.map((destination) => (
            <a
              key={destination}
              href="#"
              className="flight-route-link"
              onClick={(e) => {
                e.preventDefault();
                handleRouteClick(destination);
              }}
            >
              {originCity} to {destination} Flight
            </a>
          ))}
        </div>
        
        {/* Additional SEO routes - always show some popular routes */}
        <div className="flight-routes-additional">
          <h4>More Popular Routes</h4>
          <div className="flight-routes-grid">
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'JFK', to: 'London', fromCity: 'New York', toCity: 'London' }); }}>New York to London Flight</a>
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'LAX', to: 'Tokyo', fromCity: 'Los Angeles', toCity: 'Tokyo' }); }}>Los Angeles to Tokyo Flight</a>
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'MIA', to: 'Paris', fromCity: 'Miami', toCity: 'Paris' }); }}>Miami to Paris Flight</a>
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'SFO', to: 'Singapore', fromCity: 'San Francisco', toCity: 'Singapore' }); }}>San Francisco to Singapore Flight</a>
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'ORD', to: 'Dublin', fromCity: 'Chicago', toCity: 'Dublin' }); }}>Chicago to Dublin Flight</a>
            <a href="#" className="flight-route-link" onClick={(e) => { e.preventDefault(); onRouteClick?.({ from: 'DFW', to: 'Cancun', fromCity: 'Dallas', toCity: 'Cancun' }); }}>Dallas to Cancun Flight</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default FlightFooter;
